/**
 * Recent asks, bucketed by when they were asked (design: "Led AI Console",
 * the Ask screen's left rail — `Today`, `Yesterday`, `Last week`).
 *
 * ## Why a pure module
 *
 * The rail renders whatever this returns, so the one thing that can be wrong
 * — which day a stamp belongs to — is testable without a DOM. It is the same
 * trap `walkedLabel` and the daily log's week strip both carry a note about:
 * a bucket decided by subtracting instants says "Today" about something asked
 * at 11pm yesterday when it is 7am now, because that is 8 hours ago. Days are
 * compared as LOCAL CALENDAR DAYS.
 */

/** An entry as the rail needs it — the id is the caller's to resolve back to
 *  a full exchange. */
export interface AskHistoryRow {
  id: string;
  createdAt: string;
}

export interface AskHistoryGroup<T extends AskHistoryRow> {
  /** `Today`, `Yesterday`, `Last week`, `Earlier` — already display-ready. */
  label: string;
  entries: T[];
}

/** Local midnight of an instant. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole local days between two instants, positive when `then` is earlier. */
function daysBefore(then: Date, now: Date): number {
  const ms = startOfDay(now).getTime() - startOfDay(then).getTime();
  return Math.round(ms / 86_400_000);
}

/**
 * Which bucket a stamp falls in.
 *
 * A stamp from the FUTURE — a clock skew between the phone that asked and the
 * browser reading it — lands in `Today` rather than in a bucket that does not
 * exist. The alternative is an ask that silently disappears off the rail.
 */
function bucketOf(createdAt: string, now: Date): { rank: number; label: string } | null {
  const when = new Date(createdAt);
  if (Number.isNaN(when.getTime())) return null;
  const days = daysBefore(when, now);
  if (days <= 0) return { rank: 0, label: "Today" };
  if (days === 1) return { rank: 1, label: "Yesterday" };
  if (days <= 7) return { rank: 2, label: "Last week" };
  return { rank: 3, label: "Earlier" };
}

/**
 * Group recent asks for the rail, newest first within each bucket and
 * newest bucket first.
 *
 * An entry whose stamp will not parse is DROPPED rather than shown under a
 * guessed heading: the rail's whole job is to say when, and a row filed under
 * the wrong day is worse than one row fewer. An empty result is the caller's
 * empty state, not an error.
 */
export function groupAskHistory<T extends AskHistoryRow>(
  entries: T[],
  now: Date,
): AskHistoryGroup<T>[] {
  const byRank = new Map<number, AskHistoryGroup<T>>();
  for (const entry of entries) {
    const bucket = bucketOf(entry.createdAt, now);
    if (!bucket) continue;
    const group = byRank.get(bucket.rank) ?? { label: bucket.label, entries: [] };
    group.entries.push(entry);
    byRank.set(bucket.rank, group);
  }
  return [...byRank.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, group]) => ({
      ...group,
      entries: [...group.entries].sort(
        (left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt),
      ),
    }));
}
